import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LuCalendar, LuChevronDown, LuChevronUp, LuImage,
    LuPlug, LuSearch, LuTriangleAlert, LuDownload, LuExternalLink,
} from 'react-icons/lu';
import PageHeader from '../components/PageHeader';
import RecordPanel, { PanelSection } from '../components/RecordPanel';
import { ListRow } from '../components/ListKit';
import Select from '../components/Select';
import { exportarResultadoCsv } from '../services/exportarCsv';
import GraficoAnunciosPorDia from '../components/GraficoAnunciosPorDia';
import {
    TrafegoPagoService,
    type AgrupamentoAnuncio,
    type ContaAnuncio,
    type CriativoAnuncio,
    type DesempenhoAnuncio,
    type LeadDoAnuncio,
    type ResumoAnuncios,
    type SerieDiariaAnuncio,
} from '../services/TrafegoPagoService';
import '../components/ListKit.css';
import { useRecorte } from '../hooks/useRecorte';
import './AnunciosPage.css';

/* Anúncios — o que cada anúncio custou e o que ele vendeu.
   docs/designsystem/propostas/anuncios.html §4 (direção B, fases 1 e 2).

   A tela inteira gira em torno de uma régua: número que não se mediu não vira
   zero, vira travessão. Os três derivados chegam `null` da API quando o
   denominador é zero, e é assim que eles são mostrados. */

const COLUNAS = 'minmax(220px, 2.2fr) 110px 70px 100px 70px 110px 120px 90px';

const GRAOS: { id: AgrupamentoAnuncio; rotulo: string }[] = [
    { id: 'campanha', rotulo: 'Campanha' },
    { id: 'conjunto', rotulo: 'Conjunto' },
    { id: 'anuncio', rotulo: 'Anúncio' },
];

/* O período é do tipo que a API entende: dia-calendário, sem hora. Os presets
   são os mesmos nomes que o resto do sistema usa, para não inventar vocabulário
   de período só nesta tela. */
const PERIODOS = [
    { value: '7', label: 'Últimos 7 dias' },
    { value: '30', label: 'Últimos 30 dias' },
    { value: '90', label: 'Últimos 90 dias' },
];

type Coluna = 'nome' | 'gasto' | 'leads' | 'custoPorLead' | 'vendas' | 'custoPorVenda' | 'receita' | 'roas';

const diaISO = (d: Date) => d.toISOString().slice(0, 10);

const periodoEmDatas = (dias: number) => {
    const ate = new Date();
    const de = new Date();
    de.setDate(de.getDate() - (dias - 1));
    return { de: diaISO(de), ate: diaISO(ate) };
};

const moedaDe = (valor: number, moeda: string) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: moeda || 'BRL',
        maximumFractionDigits: 2,
    }).format(valor);

const inteiro = (n: number) => new Intl.NumberFormat('pt-BR').format(n);

/** "4 conjuntos · 11 anúncios" — no grão do conjunto, só a contagem de anúncios. */
const agregado = (l: DesempenhoAnuncio) => {
    const partes: string[] = [];
    /* Contagem 1 não entra: "1 conjunto · 5 anúncios" na linha de um conjunto
       diz que ele é ele mesmo. A frase existe para avisar que o número soma
       várias coisas — com uma só, não há o que avisar. */
    if (l.conjuntos > 1) partes.push(`${l.conjuntos} conjuntos`);
    if (l.anuncios > 1) partes.push(`${l.anuncios} anúncios`);
    return partes.join(' · ');
};

const iniciais = (nome: string) => {
    const limpo = nome.trim();
    if (!limpo) return '?';
    if (/^\+?\d[\d\s()-]*$/.test(limpo)) return limpo.replace(/\D/g, '').slice(-2) || '?';
    const partes = limpo.split(/\s+/);
    return (partes.length === 1
        ? partes[0].slice(0, 2)
        : partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
};

/** Travessão quando o denominador era zero — ver a régua no topo do arquivo. */
const Derivado = ({ valor, formatar }: { valor: number | null; formatar: (v: number) => string }) =>
    valor === null || valor === undefined
        ? <span className="anu-vazio" title="Não houve base para este cálculo no período">—</span>
        : <>{formatar(valor)}</>;

const AnunciosPage = () => {
    const navigate = useNavigate();

    const [contas, setContas] = useState<ContaAnuncio[]>([]);
    const [contaId, setContaId] = useState<number | null>(null);
    /* Recorte de trabalho: aqui o periodo e o grao custam mais caro que a
       busca - remontar "90 dias, por anuncio" e o atrito de verdade. */
    const [recorte, gravarRecorte] = useRecorte('ad:recorte', {
        dias: '30', grao: 'campanha' as AgrupamentoAnuncio, busca: '',
        ordem: { coluna: 'roas' as Coluna, desc: true },
    });
    const [dias, setDias] = useState(recorte.dias);
    const [grao, setGrao] = useState<AgrupamentoAnuncio>(recorte.grao);
    const [busca, setBusca] = useState(recorte.busca);
    const [ordem, setOrdem] = useState<{ coluna: Coluna; desc: boolean }>(recorte.ordem);
    useEffect(() => {
        gravarRecorte({ dias, grao, busca, ordem });
    }, [dias, grao, busca, ordem, gravarRecorte]);

    const [linhas, setLinhas] = useState<DesempenhoAnuncio[]>([]);
    const [resumo, setResumo] = useState<ResumoAnuncios | null>(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState<string | null>(null);
    const [aberta, setAberta] = useState<DesempenhoAnuncio | null>(null);
    const [leads, setLeads] = useState<LeadDoAnuncio[]>([]);
    const [carregandoLeads, setCarregandoLeads] = useState(false);
    const [criativo, setCriativo] = useState<CriativoAnuncio | null>(null);
    const [truncado, setTruncado] = useState(false);
    const [midiaFalhou, setMidiaFalhou] = useState(false);
    const [serie, setSerie] = useState<SerieDiariaAnuncio[]>([]);

    const filtro = useMemo(() => ({
        ...periodoEmDatas(Number(dias)),
        agruparPor: grao,
        contaAnuncioId: contaId,
    }), [dias, grao, contaId]);

    useEffect(() => {
        TrafegoPagoService.buscarContas().then(setContas).catch(() => setContas([]));
    }, []);

    const carregar = useCallback(async () => {
        setCarregando(true);
        setErro(null);
        try {
            const [d, r, s] = await Promise.all([
                TrafegoPagoService.desempenho(filtro),
                TrafegoPagoService.resumo(filtro),
                /* A série não pode derrubar a tela: se o gráfico falhar, os
                   números continuam valendo. Por isso ela falha para o lado
                   silencioso, e as outras duas não. */
                TrafegoPagoService.porDia(filtro).catch(() => [] as SerieDiariaAnuncio[]),
            ]);
            setLinhas(d);
            setResumo(r);
            setSerie(s);
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível carregar os anúncios.');
            setLinhas([]);
            setResumo(null);
        } finally {
            setCarregando(false);
        }
    }, [filtro]);

    useEffect(() => { carregar(); }, [carregar]);

    /* O painel busca sob demanda, ao abrir: puxar isso para todas as linhas junto
       com a lista seria uma consulta por linha para encher um painel que talvez
       ninguém abra. Criativo e leads vêm na MESMA chamada — o criativo está no
       mesmo join, e buscá-lo à parte obrigaria a varrer todas as atribuições da
       empresa para achar uma. */
    useEffect(() => {
        if (!aberta?.identificador) { setLeads([]); setCriativo(null); setTruncado(false); return; }
        let vivo = true;
        setCarregandoLeads(true);
        TrafegoPagoService.painelDoAnuncio(aberta.identificador, filtro.de, filtro.ate)
            .then(p => {
                if (!vivo) return;
                setLeads(p.leads);
                setCriativo(p.criativo);
                setTruncado(p.truncado);
                setMidiaFalhou(false);   // criativo novo, erro anterior não vale mais
            })
            .catch(() => { if (vivo) { setLeads([]); setCriativo(null); setTruncado(false); } })
            .finally(() => { if (vivo) setCarregandoLeads(false); });
        return () => { vivo = false; };
    }, [aberta, filtro.de, filtro.ate]);

    /* A moeda da tela é a da conta escolhida; com "todas" e uma moeda só, é a
       dela. Com mais de uma, o resumo vem quebrado e a faixa se repete — nunca
       se converte câmbio aqui. */
    const moeda = resumo?.multiMoeda ? '' : (resumo?.moeda || contas[0]?.moeda || 'BRL');

    const visiveis = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        const filtradas = termo
            ? linhas.filter(l => (l.nome ?? '').toLowerCase().includes(termo))
            : linhas;

        const sinal = ordem.desc ? -1 : 1;
        return [...filtradas].sort((a, b) => {
            if (ordem.coluna === 'nome') {
                return sinal * (a.nome ?? '').localeCompare(b.nome ?? '', 'pt-BR');
            }
            const va = a[ordem.coluna];
            const vb = b[ordem.coluna];
            /* Linha sem o derivado vai para o fim em qualquer direção: "não se
               mediu" não é nem o maior nem o menor valor. */
            if (va === null && vb === null) return 0;
            if (va === null) return 1;
            if (vb === null) return -1;
            return sinal * (va - vb);
        });
    }, [linhas, busca, ordem]);

    /* O total é a lista MAIS o que ficou fora dela. Os derivados se recalculam
       aqui em vez de virem do resumo porque o resumo do servidor, de propósito,
       só conta o que teve gasto — e este total conta os dois. Com várias moedas
       não existe total único: a faixa se repete por moeda e a linha some. */
    const totalGeral = useMemo(() => {
        if (!resumo || resumo.multiMoeda) return null;
        const semCusto = resumo.leadsSemCusto + resumo.vendasSemCusto + resumo.receitaSemCusto;
        const gasto = resumo.gastoTotal;
        const leads = resumo.leadsTotal + resumo.leadsSemCusto;
        const vendas = resumo.vendasTotal + resumo.vendasSemCusto;
        const receita = resumo.receitaTotal + resumo.receitaSemCusto;
        return {
            gasto, leads, vendas, receita,
            /* O gasto NÃO cresce com os de fora (eles não têm gasto), então dividir
               por todos os leads daria um CPL menor que a verdade. O denominador
               continua sendo só quem teve custo. */
            cpl: resumo.leadsTotal > 0 ? gasto / resumo.leadsTotal : null,
            cpv: resumo.vendasTotal > 0 ? gasto / resumo.vendasTotal : null,
            roas: gasto > 0 ? receita / gasto : null,
            temSemCusto: semCusto > 0,
        };
    }, [resumo]);

    /* Exporta o que está na tela: mesmo grão, mesma ordem, mesmo filtro. Vai o
       valor CRU — "R$ 1.028,00" numa planilha é texto e a coluna deixa de somar
       (a regra é do `exportarResultadoCsv`, que já resolve BOM e separador). */
    const exportar = () => {
        const nomeGrao = GRAOS.find(g => g.id === grao)?.rotulo ?? 'Campanha';
        exportarResultadoCsv({
            colunas: [
                { id: 'nome', nome: nomeGrao, tipo: 'texto' },
                { id: 'gasto', nome: `Investido (${moeda})`, tipo: 'decimal' },
                { id: 'leads', nome: 'Leads', tipo: 'inteiro' },
                { id: 'cpl', nome: 'CPL', tipo: 'decimal' },
                { id: 'vendas', nome: 'Vendas', tipo: 'inteiro' },
                { id: 'cpv', nome: 'Custo por venda', tipo: 'decimal' },
                { id: 'receita', nome: 'Receita', tipo: 'decimal' },
                { id: 'roas', nome: 'ROAS', tipo: 'decimal' },
            ],
            linhas: [
                ...visiveis.map(l => [
                    l.nome ?? l.identificador ?? 'Sem nome ainda',
                    l.gasto, l.leads, l.custoPorLead, l.vendas, l.custoPorVenda, l.receita, l.roas,
                ]),
                /* A linha de fora da conta vai junto: quem abrir a planilha tem que
                   ver a mesma diferença que a tela mostra, senão o CSV "conserta"
                   um número que não está certo. */
                ...((resumo?.leadsSemCusto ?? 0) > 0
                    ? [['Sem custo no período', null, resumo!.leadsSemCusto, null,
                        resumo!.vendasSemCusto, null, resumo!.receitaSemCusto, null]]
                    : []),
            ],
            totais: totalGeral
                ? ['Total', totalGeral.gasto, totalGeral.leads, totalGeral.cpl,
                   totalGeral.vendas, totalGeral.cpv, totalGeral.receita, totalGeral.roas]
                : [],
            rotulo: `Anúncios por ${nomeGrao.toLowerCase()} · ${filtro.de} a ${filtro.ate}`,
            avisos: [],
            truncado: false,
        }, `anuncios_${grao}`);
    };

    const ordenarPor = (coluna: Coluna) => setOrdem(o =>
        o.coluna === coluna ? { coluna, desc: !o.desc } : { coluna, desc: true });

    const Cabecalho = ({ coluna, children }: { coluna: Coluna; children: React.ReactNode }) => (
        <button
            type="button"
            className={`anu-ord ${ordem.coluna === coluna ? 'is-ativa' : ''}`}
            onClick={() => ordenarPor(coluna)}
        >
            {children}
            {ordem.coluna === coluna && (ordem.desc
                ? <LuChevronDown className="anu-ord-seta" size={12} />
                : <LuChevronUp className="anu-ord-seta" size={12} />)}
        </button>
    );

    /* A saúde da conta vem do status de sincronização que a própria listagem de
       contas já devolve. Tudo que não é "Ativa" é alerta: token expirado e
       permissão revogada param de atualizar o gasto em silêncio, e o número
       continua na tela parecendo atual. */
    const contasComProblema = contas.filter(c => c.statusSincronizacao !== 'Ativa');
    const ultimaSync = contas
        .map(c => c.ultimaSincronizacaoEm)
        .filter((d): d is string => !!d)
        .sort()
        .pop();

    const desdeUltimaSync = () => {
        if (!ultimaSync) return 'ainda não sincronizado';
        const horas = Math.floor((Date.now() - new Date(ultimaSync).getTime()) / 36e5);
        if (horas < 1) return 'sincronizado há menos de 1 h';
        if (horas < 24) return `sincronizado há ${horas} h`;
        return `sincronizado há ${Math.floor(horas / 24)} d`;
    };

    const resumosVisiveis: ResumoAnuncios[] = resumo
        ? (resumo.multiMoeda ? (resumo.resumoPorMoeda ?? []) : [resumo])
        : [];

    const semConta = !carregando && contas.length === 0;

    const FaixaNumeros = ({ r }: { r: ResumoAnuncios }) => (
        <div className="anu-kpis">
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Investimento</div>
                <div className="anu-kpi-valor">{moedaDe(r.gastoTotal, r.moeda)}</div>
                <div className="anu-kpi-nota">{PERIODOS.find(p => p.value === dias)?.label.toLowerCase()}</div>
            </div>
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Leads</div>
                <div className="anu-kpi-valor">{inteiro(r.leadsTotal)}</div>
                {/* O número grande conta só quem teve custo — é o que sustenta o CPL
                    logo ao lado. Os de fora aparecem aqui, senão o KPI e a linha de
                    Total mostram números diferentes sem nada explicando por quê. */}
                <div className="anu-kpi-nota">
                    {r.leadsSemCusto > 0 ? `+${inteiro(r.leadsSemCusto)} sem custo` : ''}
                </div>
            </div>
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Custo por lead</div>
                <div className="anu-kpi-valor"><Derivado valor={r.custoPorLead} formatar={v => moedaDe(v, r.moeda)} /></div>
                <div className="anu-kpi-nota" />
            </div>
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Vendas</div>
                <div className="anu-kpi-valor">{inteiro(r.vendasTotal)}</div>
                <div className="anu-kpi-nota">fechadas no período</div>
            </div>
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Custo por venda</div>
                <div className="anu-kpi-valor"><Derivado valor={r.custoPorVenda} formatar={v => moedaDe(v, r.moeda)} /></div>
                <div className="anu-kpi-nota" />
            </div>
            <div className="anu-kpi">
                <div className="anu-kpi-rotulo">Receita</div>
                <div className="anu-kpi-valor">{moedaDe(r.receitaTotal, r.moeda)}</div>
                <div className="anu-kpi-nota" />
            </div>
            <div className="anu-kpi is-forte">
                <div className="anu-kpi-rotulo">ROAS</div>
                <div className="anu-kpi-valor">
                    <Derivado valor={r.roas} formatar={v => `${v.toFixed(2).replace('.', ',')}×`} />
                </div>
                <div className="anu-kpi-nota">retorno sobre a mídia</div>
            </div>
        </div>
    );

    return (
        <div className="page-shell lk-screen">
            <PageHeader
                title="Anúncios"
                subtitle="O que cada anúncio custou e o que ele vendeu"
                actions={
                    <div className="lk-actions">
                        {contas.length > 1 && (
                            <Select
                                size="control"
                                block={false}
                                icon={<LuPlug size={14} />}
                                value={contaId === null ? 'todas' : String(contaId)}
                                options={[
                                    { value: 'todas', label: 'Todas as contas' },
                                    ...contas.map(c => ({
                                        value: String(c.contaAnuncioId),
                                        label: `${c.nome} (${c.moeda})`,
                                    })),
                                ]}
                                onChange={v => setContaId(v === 'todas' ? null : Number(v))}
                                ariaLabel="Conta de anúncios"
                            />
                        )}
                        <Select
                            size="control"
                            block={false}
                            icon={<LuCalendar size={14} />}
                            value={dias}
                            options={PERIODOS}
                            onChange={setDias}
                            ariaLabel="Período"
                        />
                    </div>
                }
            />

            <div className="page-body lk-fill">
                {semConta ? (
                    /* Estado vazio com UMA porta: a tela não sabe conectar conta, quem
                       sabe é Conexões. Repetir o botão aqui criaria dois caminhos para
                       o mesmo lugar. */
                    <div className="lk-blank">
                        <p className="lk-blank-title">Nenhuma conta de anúncios conectada</p>
                        <p className="lk-blank-text">
                            Os números desta tela vêm da sua conta do Meta Ads. Conecte uma conta
                            em Apps e o desempenho aparece aqui na próxima sincronização.
                        </p>
                        <button type="button" className="lk-btn is-primary" onClick={() => navigate('/apps')}>
                            <LuPlug size={14} /> Ir para Apps
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Com erro a faixa some: ela é contexto para ler o número, e
                            sem número na tela vira cromo — pior ainda no 403, onde
                            promete informação a quem não pode ver nenhuma. */}
                        {!erro && (
                        <div className={`anu-saude ${contasComProblema.length > 0 ? 'is-alerta' : ''}`}>
                            <span className="anu-saude-ponto" />
                            <span className="anu-saude-texto">
                                {contasComProblema.length > 0 ? (
                                    <>
                                        {contasComProblema.length === 1
                                            ? `A conta ${contasComProblema[0].nome} parou de sincronizar`
                                            : `${contasComProblema.length} contas pararam de sincronizar`}
                                        {' '}— o gasto abaixo está desatualizado.
                                    </>
                                ) : (
                                    <>
                                        {desdeUltimaSync()} · a Meta revisa os números dos últimos 7 dias,
                                        então ontem ainda pode mudar
                                    </>
                                )}
                            </span>
                            <button type="button" className="anu-saude-link" onClick={() => navigate('/connections')}>
                                {contasComProblema.length > 0 ? <LuTriangleAlert size={13} /> : <LuPlug size={13} />}
                                Gerenciar conta
                            </button>
                        </div>
                        )}

                        {erro ? (
                            <div className="lk-blank">
                                <p className="lk-blank-title">{erro}</p>
                                <button type="button" className="lk-btn" onClick={carregar}>Tentar de novo</button>
                            </div>
                        ) : (
                            <>
                                {resumo?.multiMoeda && (
                                    <p className="anu-regua" style={{ margin: '0 2px 10px' }}>
                                        Há contas em moedas diferentes no período. Os totais não são somados —
                                        cada moeda tem a própria faixa.
                                    </p>
                                )}

                                {resumosVisiveis.map(r => (
                                    <div key={r.moeda || 'única'}>
                                        {resumo?.multiMoeda && <div className="anu-moeda-titulo">{r.moeda}</div>}
                                        <FaixaNumeros r={r} />
                                    </div>
                                ))}

                                <p className="anu-regua">
                                    Leads pela data de entrada. Vendas pela data de fechamento — incluem leads
                                    que entraram antes do período. Atribuição de último clique.
                                </p>

                                {/* O gráfico entra DEPOIS dos números e ANTES da lista: a
                                    primeira pergunta é "qual campanha vale a pena", que a
                                    lista responde; a linha do tempo é a segunda. Some com
                                    período curto (dois pontos não fazem tendência) e com
                                    moedas misturadas (somar gasto de moedas diferentes numa
                                    linha desenha um número que não existe). */}
                                {serie.length > 6 && !resumo?.multiMoeda && (
                                    <div className="anu-grafico">
                                        <div className="anu-grafico-topo">
                                            <span className="anu-grafico-t">Investimento e leads por dia</span>
                                            <span className="anu-grafico-s">
                                                barra = investido · linha = leads
                                            </span>
                                        </div>
                                        <GraficoAnunciosPorDia serie={serie} moeda={moeda} />
                                    </div>
                                )}

                                <div className="anu-controles">
                                    <div className="anu-grao" role="group" aria-label="Agrupar por">
                                        {GRAOS.map(g => (
                                            <button
                                                key={g.id}
                                                type="button"
                                                className={grao === g.id ? 'is-on' : ''}
                                                onClick={() => setGrao(g.id)}
                                            >
                                                {g.rotulo}
                                            </button>
                                        ))}
                                    </div>
                                    <label className="lk-search">
                                        <LuSearch size={14} />
                                        <input
                                            type="search"
                                            placeholder="Buscar pelo nome…"
                                            value={busca}
                                            onChange={e => setBusca(e.target.value)}
                                        />
                                    </label>
                                    <span className="lk-count">
                                        {visiveis.length} {visiveis.length === 1 ? 'linha' : 'linhas'}
                                    </span>
                                    <button
                                        type="button"
                                        className="lk-btn"
                                        onClick={exportar}
                                        disabled={carregando || visiveis.length === 0}
                                    >
                                        <LuDownload size={14} /> Exportar CSV
                                    </button>
                                </div>

                                <div className="lk-table">
                                    <div className="lk-scroll">
                                        <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                            <div className="lk-head"><Cabecalho coluna="nome">{GRAOS.find(g => g.id === grao)?.rotulo}</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="gasto">Investido</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="leads">Leads</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="custoPorLead">CPL</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="vendas">Vendas</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="custoPorVenda">Custo/venda</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="receita">Receita</Cabecalho></div>
                                            <div className="lk-head anu-head-num"><Cabecalho coluna="roas">ROAS</Cabecalho></div>
                                        </div>
                                        <div className="lk-body">
                                            {carregando
                                                ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                                : visiveis.length === 0
                                                    ? (
                                                        <p className="lk-blank-text" style={{ padding: '24px 14px' }}>
                                                            Nenhum anúncio com gasto neste período.
                                                        </p>
                                                    )
                                                    : visiveis.map(l => (
                                                        <ListRow
                                                            key={l.identificador ?? l.nome ?? Math.random()}
                                                            columns={COLUNAS}
                                                            onOpen={() => setAberta(l)}
                                                            label={`Abrir ${l.nome ?? 'anúncio sem nome'}`}
                                                        >
                                                            <span className="lk-cell lk-strong">
                                                                <span>
                                                                    {l.nome || <span className="lk-tag">Sem nome ainda</span>}
                                                                    {!l.nome && l.identificador && (
                                                                        <span className="anu-nome-sub">{l.identificador}</span>
                                                                    )}
                                                                    {/* O que a linha agrega. Só nos grãos em que agrega
                                                                        alguma coisa: no anúncio os dois são 1 e a frase
                                                                        viraria "1 conjunto · 1 anúncio", que é ruído. */}
                                                                    {grao !== 'anuncio' && agregado(l) && (
                                                                        <span className="anu-nome-sub">{agregado(l)}</span>
                                                                    )}
                                                                </span>
                                                            </span>
                                                            <span className="lk-cell anu-num">{moedaDe(l.gasto, l.moeda || moeda)}</span>
                                                            <span className="lk-cell anu-num">{inteiro(l.leads)}</span>
                                                            <span className="lk-cell anu-num">
                                                                <Derivado valor={l.custoPorLead} formatar={v => moedaDe(v, l.moeda || moeda)} />
                                                            </span>
                                                            <span className="lk-cell anu-num">{inteiro(l.vendas)}</span>
                                                            <span className="lk-cell anu-num">
                                                                <Derivado valor={l.custoPorVenda} formatar={v => moedaDe(v, l.moeda || moeda)} />
                                                            </span>
                                                            <span className="lk-cell anu-num">{moedaDe(l.receita, l.moeda || moeda)}</span>
                                                            <span className="lk-cell anu-num">
                                                                <Derivado valor={l.roas} formatar={v => `${v.toFixed(2).replace('.', ',')}×`} />
                                                            </span>
                                                        </ListRow>
                                                    ))}

                                            {/* O item 3.1 virando linha: leads de anúncios que não
                                                tiveram gasto no período. Não entram no CPL nem no ROAS
                                                — não há gasto para dividir —, mas aparecem, senão o
                                                total da tela fica menor que o que o CRM recebeu e a
                                                diferença some sem explicação. */}
                                            {!carregando && (resumo?.leadsSemCusto ?? 0) > 0 && (
                                                <div className="lk-row anu-sem-custo" style={{ gridTemplateColumns: COLUNAS }}>
                                                    <span className="lk-cell">
                                                        <span>
                                                            <span className="lk-tag">Sem custo no período</span>
                                                            <span className="anu-nome-sub">
                                                                {resumo!.anunciosSemCusto} {resumo!.anunciosSemCusto === 1 ? 'anúncio' : 'anúncios'} sem gasto nestes {dias} dias
                                                            </span>
                                                        </span>
                                                    </span>
                                                    <span className="lk-cell anu-num anu-vazio">—</span>
                                                    <span className="lk-cell anu-num">{inteiro(resumo!.leadsSemCusto)}</span>
                                                    <span className="lk-cell anu-num anu-vazio">—</span>
                                                    <span className="lk-cell anu-num">{inteiro(resumo!.vendasSemCusto)}</span>
                                                    <span className="lk-cell anu-num anu-vazio">—</span>
                                                    <span className="lk-cell anu-num">{moedaDe(resumo!.receitaSemCusto, moeda)}</span>
                                                    <span className="lk-cell anu-num anu-vazio">—</span>
                                                </div>
                                            )}

                                            {/* O total soma a lista MAIS o que ficou fora dela: é o
                                                número que o gestor compara com o extrato. Os derivados
                                                continuam vindo do servidor, calculados só sobre o que
                                                teve gasto. */}
                                            {!carregando && totalGeral && (
                                                <div className="lk-row anu-total" style={{ gridTemplateColumns: COLUNAS }}>
                                                    <span className="lk-cell">Total</span>
                                                    <span className="lk-cell anu-num">{moedaDe(totalGeral.gasto, moeda)}</span>
                                                    <span className="lk-cell anu-num">{inteiro(totalGeral.leads)}</span>
                                                    <span className="lk-cell anu-num">
                                                        <Derivado valor={totalGeral.cpl} formatar={v => moedaDe(v, moeda)} />
                                                    </span>
                                                    <span className="lk-cell anu-num">{inteiro(totalGeral.vendas)}</span>
                                                    <span className="lk-cell anu-num">
                                                        <Derivado valor={totalGeral.cpv} formatar={v => moedaDe(v, moeda)} />
                                                    </span>
                                                    <span className="lk-cell anu-num">{moedaDe(totalGeral.receita, moeda)}</span>
                                                    <span className="lk-cell anu-num">
                                                        <Derivado valor={totalGeral.roas} formatar={v => `${v.toFixed(2).replace('.', ',')}×`} />
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </>
                )}
            </div>

            <RecordPanel
                open={aberta !== null}
                onClose={() => setAberta(null)}
                title={aberta?.nome || 'Sem nome ainda'}
                subtitle={aberta?.identificador ?? undefined}
            >
                {aberta && (
                    <>
                        <div className="anu-pn-nums">
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">Investido</div>
                                <div className="anu-pn-num-v">{moedaDe(aberta.gasto, aberta.moeda || moeda)}</div>
                            </div>
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">Leads</div>
                                <div className="anu-pn-num-v">{inteiro(aberta.leads)}</div>
                            </div>
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">CPL</div>
                                <div className="anu-pn-num-v">
                                    <Derivado valor={aberta.custoPorLead} formatar={v => moedaDe(v, aberta.moeda || moeda)} />
                                </div>
                            </div>
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">Vendas</div>
                                <div className="anu-pn-num-v">{inteiro(aberta.vendas)}</div>
                            </div>
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">Receita</div>
                                <div className="anu-pn-num-v">{moedaDe(aberta.receita, aberta.moeda || moeda)}</div>
                            </div>
                            <div className="anu-pn-num">
                                <div className="anu-pn-num-r">ROAS</div>
                                <div className="anu-pn-num-v">
                                    <Derivado valor={aberta.roas} formatar={v => `${v.toFixed(2).replace('.', ',')}×`} />
                                </div>
                            </div>
                        </div>

                        <PanelSection title="Entrega">
                            <div className="anu-pn-nums">
                                <div className="anu-pn-num">
                                    <div className="anu-pn-num-r">Impressões</div>
                                    <div className="anu-pn-num-v">{inteiro(aberta.impressoes)}</div>
                                </div>
                                <div className="anu-pn-num">
                                    <div className="anu-pn-num-r">Cliques</div>
                                    <div className="anu-pn-num-v">{inteiro(aberta.cliques)}</div>
                                </div>
                                <div className="anu-pn-num">
                                    <div className="anu-pn-num-r">Lead por clique</div>
                                    <div className="anu-pn-num-v">
                                        <Derivado
                                            valor={aberta.cliques > 0 ? (aberta.leads / aberta.cliques) * 100 : null}
                                            formatar={v => `${v.toFixed(1).replace('.', ',')}%`}
                                        />
                                    </div>
                                </div>
                            </div>
                        </PanelSection>

                        {/* O criativo só existe no grão do ANÚNCIO: ele vem da atribuição de
                            quem chegou, e uma campanha tem vários. Agrupado por campanha ou
                            conjunto a seção não aparece, em vez de mostrar um criativo
                            qualquer como se fosse o da linha inteira. */}
                        {criativo && (
                            <PanelSection title="O anúncio que o cliente viu">
                                <div className="anu-criativo">
                                    {/* A imagem da Meta expira e passa a dar 404 — já visto na base.
                                        Sem o fallback, o criativo fica com um quadrado vazio do lado
                                        do texto, que lê como bug em vez de "a imagem saiu do ar". */}
                                    {criativo.midiaUrl && !midiaFalhou
                                        ? <img
                                            className="anu-criativo-img"
                                            src={criativo.midiaUrl}
                                            alt=""
                                            onError={() => setMidiaFalhou(true)}
                                          />
                                        : <div className="anu-criativo-img"><LuImage size={18} /></div>}
                                    <div className="anu-criativo-tx">
                                        <div className="anu-criativo-tt">{criativo.titulo || 'Sem título'}</div>
                                        {criativo.corpo && <div className="anu-criativo-cp">{criativo.corpo}</div>}
                                        {criativo.urlOrigem && (
                                            <div className="anu-criativo-ur">{criativo.urlOrigem}</div>
                                        )}
                                    </div>
                                </div>
                            </PanelSection>
                        )}

                        <PanelSection
                            title="Conversas geradas"
                            extra={leads.length > 0
                                ? <span className="anu-pn-conta">{aberta.leads}</span>
                                : undefined}
                        >
                            {carregandoLeads ? (
                                <p className="anu-pn-vazio">Carregando…</p>
                            ) : leads.length === 0 ? (
                                /* Zero leads com gasto no período é informação, não erro: diz
                                   que o anúncio entregou e ninguém chamou. */
                                <p className="anu-pn-vazio">
                                    Nenhuma conversa veio deste anúncio no período.
                                </p>
                            ) : (
                                <>
                                    {leads.map(l => (
                                        <button
                                            key={l.idConversa}
                                            type="button"
                                            className="anu-lead"
                                            onClick={() => navigate(`/pipeline/deal/${l.idConversa}`)}
                                        >
                                            <span className="anu-lead-av">{iniciais(l.nomeContato || l.numeroWhatsapp)}</span>
                                            <span className="anu-lead-tx">
                                                <span className="anu-lead-nome">{l.nomeContato || l.numeroWhatsapp}</span>
                                                <span className="anu-lead-sub">
                                                    {l.statusDescricao}
                                                    {l.valor > 0 && ` · ${moedaDe(l.valor, moeda)}`}
                                                    {l.nomeResponsavel && ` · ${l.nomeResponsavel}`}
                                                </span>
                                            </span>
                                            <LuExternalLink className="anu-lead-ir" size={13} />
                                        </button>
                                    ))}
                                    {/* A lista é amostra. O total NÃO é repetido daqui: ele vem da
                                        linha da lista e esta lista vem de outra chamada — quando os
                                        dois divergem (período recarregado, ordem de chegada) sai
                                        "Mostrando 50 de 0", que é pior que não dizer o total.
                                        A linha ao lado já mostra quantos são. */}
                                    {truncado && (
                                        <p className="anu-pn-vazio">
                                            Mostrando as {leads.length} mais recentes.
                                        </p>
                                    )}
                                </>
                            )}
                        </PanelSection>
                    </>
                )}
            </RecordPanel>
        </div>
    );
};

export default AnunciosPage;
